import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { BreakableEmail } from "../UI/Table/BreakableEmail.tsx";
import { BreakableText } from "../UI/Table/BreakableText.tsx";
import { BasicTable } from "../UI/Table/BasicTable.tsx";
import { EmptyTableMessage } from "../UI/Table/EmptyTableMessage.tsx";
import { formatDate, formatDateTime } from "../UI/Utils/formatDate.ts";
import { GetOneOnOnePlanPurchasesQuery } from "../gql/graphql.ts";

type OneOnOnePlanPurchase =
  GetOneOnOnePlanPurchasesQuery["getOneOnOnePlanPurchases"][number];

const HIDDEN_ON_MOBILE = { className: "hidden md:table-cell" };

const columnHelper = createColumnHelper<OneOnOnePlanPurchase>();

function describeGroupPlan(purchase: OneOnOnePlanPurchase): string {
  if (purchase.hasActiveGroupPlan) return "Sì";
  if (purchase.lastGroupPlanEndDate)
    return `No (scaduto il ${formatDate(purchase.lastGroupPlanEndDate, { locale: "it" })})`;
  return "No";
}

const columns = [
  columnHelper.accessor("orderId", {
    header: "Order ID",
    meta: HIDDEN_ON_MOBILE,
    cell: (info) => (
      <BreakableText className={"font-mono text-xs"}>
        {info.getValue() ?? "—"}
      </BreakableText>
    ),
  }),
  columnHelper.accessor("userEmail", {
    header: "Email utente",
    cell: (info) => <BreakableEmail email={info.getValue()} />,
  }),
  columnHelper.accessor("purchasedAt", {
    header: "Acquistato il",
    meta: HIDDEN_ON_MOBILE,
    cell: (info) => (
      <span className={"whitespace-nowrap"}>
        {formatDateTime(info.getValue(), { locale: "it" })}
      </span>
    ),
  }),
  columnHelper.accessor("onboardingFormCompleted", {
    header: "Onboarding completato",
    cell: (info) => (info.getValue() ? "Sì" : "No"),
  }),
  columnHelper.display({
    id: "groupPlan",
    header: "Piano di gruppo attivo",
    meta: HIDDEN_ON_MOBILE,
    cell: ({ row }) => describeGroupPlan(row.original),
  }),
];

export function OneOnOnePlanPurchasesTable({
  purchases,
}: {
  purchases: OneOnOnePlanPurchase[];
}) {
  const table = useReactTable({
    columns,
    data: purchases,
    getCoreRowModel: getCoreRowModel(),
  });
  if (purchases.length === 0)
    return <EmptyTableMessage message={"Nessun acquisto trovato"} />;
  return (
    <BasicTable
      table={table}
      compact
      scrollableMinWidthClassName={"min-w-full"}
    />
  );
}
